import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faLink } from "@fortawesome/free-solid-svg-icons";
import { POINT_VALUES, cardLabel, formatVoteDisplay } from "./cards";
import { FrequencyBars } from "./FrequencyBars";
import {
  readStoredIdentity,
  usePointingBlackjack,
} from "./PointingBlackjackProvider";
import { lobbyPath, sessionPath } from "./paths";
import { isValidRoomCode } from "./roomCode";
import { PointyFeedbackModal } from "./PointyFeedbackModal";
import { statusWithCountLabel } from "./liveVoteCounts";
import { playersAtTable } from "./playersAtTable";
import { showNoVoteFrown } from "./showNoVoteFrown";
import { teamVoteParticipation } from "./teamVoteParticipation";
import type { RoleParticipation, TeamVoteParticipation } from "./teamVoteParticipation";
import { canToggleFunNames, funDisplayName } from "./funName";
import type { PlayerRole, PlayerRow, VoteValue } from "./types";
import {
  JOIN_BUTTONS,
  MISSING_START_BUTTONS,
  SessionEntryForm,
} from "./SessionEntryForm";

type RoomPhase = "loading" | "unreachable" | "invalid" | "missing" | "exists";

function buildCounts(
  voteByPlayer: Record<string, number | null | "hidden">,
  playerIds: string[]
): Record<VoteValue, number> {
  const out: Record<VoteValue, number> = {
    1: 0,
    2: 0,
    3: 0,
    5: 0,
    8: 0,
    13: 0,
  };
  for (const pid of playerIds) {
    const v = voteByPlayer[pid];
    if (typeof v === "number" && (POINT_VALUES as readonly number[]).includes(v)) {
      out[v as VoteValue] += 1;
    }
  }
  return out;
}

/** Clipboard API requires a secure context (HTTPS or localhost); use fallbacks on LAN HTTP. */
async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // continue to fallbacks
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    ta.style.top = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** Whether this player has submitted a vote (values stay hidden until reveal). */
function hasSubmittedVote(
  vote: number | null | "hidden" | undefined
): boolean {
  return vote === "hidden" || typeof vote === "number";
}

function averageVote(
  voteByPlayer: Record<string, number | null | "hidden">,
  playerIds: string[]
): number | null {
  const nums: number[] = [];
  for (const pid of playerIds) {
    const v = voteByPlayer[pid];
    if (typeof v === "number") nums.push(v);
  }
  if (!nums.length) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

const ROLE_AVG_ORDER: PlayerRole[] = ["dev", "qa", "product"];

const ROLE_AVG_LABEL: Record<PlayerRole, string> = {
  dev: "Dev",
  qa: "QA",
  product: "Product",
};

function formatAverage(value: number | null): string {
  return value === null ? "—" : value.toFixed(2);
}

function averageVoteForRole(
  players: PlayerRow[],
  voteByPlayer: Record<string, number | null | "hidden">,
  role: PlayerRole
): number | null {
  const ids = players.filter((p) => p.role === role).map((p) => p.id);
  return averageVote(voteByPlayer, ids);
}

const VoteCardGrid: React.FC<{
  myNumeric: number | undefined;
  vote: (value: number) => void;
  clearVote: () => void;
}> = ({ myNumeric, vote, clearVote }) => (
  <div className="pb-card-grid">
    {POINT_VALUES.map((v) => {
      const { main, sub } = cardLabel(v);
      const selected = myNumeric === v;
      return (
        <button
          key={v}
          type="button"
          className={`pb-card ${selected ? "pb-card--selected" : ""}`}
          onClick={() => {
            if (selected) clearVote();
            else vote(v);
          }}
        >
          <span className="pb-card__corner">{main}</span>
          {sub ? <span className="pb-card__sub">{sub}</span> : null}
        </button>
      );
    })}
  </div>
);

const TeamVotePercent: React.FC<{
  participation: TeamVoteParticipation | null;
}> = ({ participation }) => {
  if (!participation) return null;
  const rows: { role: "dev" | "qa"; label: string; stats: RoleParticipation }[] =
    [];
  if (participation.dev) {
    rows.push({ role: "dev", label: "Dev", stats: participation.dev });
  }
  if (participation.qa) {
    rows.push({ role: "qa", label: "QA", stats: participation.qa });
  }
  if (rows.length === 0) return null;

  return (
    <div className="pb-team-vote-percent" aria-live="polite">
      {rows.map(({ role, label, stats }) => (
        <div
          key={role}
          className={`pb-team-vote-percent__row pb-team-vote-percent__row--${role}`}
          aria-label={`${stats.percent} percent of ${label} have voted, ${stats.voted} of ${stats.total}`}
        >
          <div className="pb-team-vote-percent__meta">
            <span className={`pb-flair pb-flair--${role}`}>{label}</span>
            <span className="pb-team-vote-percent__value">{stats.percent}%</span>
            <span className="pb-team-vote-percent__detail">
              {stats.voted}/{stats.total}
            </span>
          </div>
          <div
            className="pb-team-vote-percent__track"
            role="presentation"
            aria-hidden
          >
            <div
              className="pb-team-vote-percent__fill"
              style={{ width: `${stats.percent}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

function PlayerStatusDot({ online, brb }: { online: boolean; brb?: boolean }) {
  const kind = brb ? "brb" : online ? "on" : "off";
  const title =
    brb ? "Be right back" : online ? "Online" : "Offline";
  return (
    <span
      className={`pb-dot pb-dot--${kind}`}
      title={title}
      aria-hidden
    />
  );
}

function RoleFlair({ role }: { role?: PlayerRole }) {
  if (role === "product") {
    return (
      <span className="pb-flair pb-flair--product" aria-label="Product">
        Product
      </span>
    );
  }
  if (role === "qa") {
    return (
      <span className="pb-flair pb-flair--qa" aria-label="QA">
        QA
      </span>
    );
  }
  if (role === "dev") {
    return (
      <span className="pb-flair pb-flair--dev" aria-label="Dev">
        Dev
      </span>
    );
  }
  return null;
}

function PlayerNameCell({
  player,
  brb,
  funNames,
}: {
  player: PlayerRow;
  brb?: boolean;
  funNames: boolean;
}) {
  return (
    <span className="pb-player-line">
      <PlayerStatusDot online={player.online} brb={brb} />
      <span className="pb-player-line__name">
        {player.name}
        {funNames && player.funTitle?.trim() ? (
          <span className="pb-player-line__fun"> the {player.funTitle.trim()}</span>
        ) : null}
      </span>
      <span className="pb-player-line__flair">
        <RoleFlair role={player.role} />
      </span>
    </span>
  );
}

function YouCard({
  name,
  funLabel,
  role,
  onSave,
}: {
  name: string;
  funLabel?: string;
  role?: PlayerRole;
  onSave: (name: string) => void;
}) {
  const [draft, setDraft] = useState(name);

  useEffect(() => {
    setDraft(name);
  }, [name]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== name) onSave(trimmed);
    else setDraft(name);
  };

  return (
    <section className="pb-panel pb-you">
      <h2>You</h2>
      <label className="pb-label pb-you__label">
        <div className="pb-you__row">
          <input
            className="pb-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
                (e.target as HTMLInputElement).blur();
              }
            }}
            maxLength={40}
            autoComplete="nickname"
          />
          <RoleFlair role={role} />
        </div>
      </label>
      {funLabel && funLabel !== name ? (
        <p className="pb-muted pb-you__fun-name">Shown as {funLabel}</p>
      ) : null}
    </section>
  );
}

function FunNamesOptions({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}) {
  return (
    <section className="pb-panel pb-options">
      <h2>Options</h2>
      <label className="pb-check">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        Fun Names
      </label>
      <p className="pb-check__hint">Show names like Bob the Sleepy Eagle.</p>
    </section>
  );
}

export const PointingBlackjackSession: React.FC = () => {
  const { sessionId: paramId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const {
    state,
    lastError,
    joinSession,
    createSession,
    querySessionExists,
    clearLastError,
    vote,
    clearVote,
    reveal,
    newRound,
    leaveTable,
    setBrb,
    setFunNames,
    updateName,
    connectionStatus,
  } = usePointingBlackjack();

  const [copied, setCopied] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const autoJoinTried = useRef(false);
  const [roomPhase, setRoomPhase] = useState<RoomPhase>("loading");
  /** Bumps the room probe when the user retries after a connection failure. */
  const [probeNonce, setProbeNonce] = useState(0);
  const stateRef = useRef(state);
  stateRef.current = state;

  const retryRoomProbe = useCallback(() => {
    leaveTable();
    setProbeNonce((n) => n + 1);
  }, [leaveTable]);

  const sessionUrl = useMemo(() => {
    if (!paramId) return "";
    return `${window.location.origin}${sessionPath(paramId)}`;
  }, [paramId]);

  const copyLink = useCallback(() => {
    if (!sessionUrl) return;
    void (async () => {
      const ok = await copyTextToClipboard(sessionUrl);
      if (ok) {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } else {
        window.prompt("Copy this invite link:", sessionUrl);
      }
    })();
  }, [sessionUrl]);

  useEffect(() => {
    autoJoinTried.current = false;
  }, [paramId]);

  useEffect(() => {
    if (!paramId) return;
    if (state?.sessionId === paramId) return;
    let cancelled = false;
    if (!isValidRoomCode(paramId)) {
      return;
    }
    setRoomPhase("loading");
    void querySessionExists(paramId).then((r) => {
      if (cancelled) return;
      if (stateRef.current?.sessionId === paramId) return;
      if (r.unreachable) setRoomPhase("unreachable");
      else if (r.invalid) setRoomPhase("invalid");
      else if (r.exists) setRoomPhase("exists");
      else setRoomPhase("missing");
    });
    return () => {
      cancelled = true;
    };
  }, [paramId, querySessionExists, state?.sessionId, probeNonce]);

  useEffect(() => {
    if (!paramId) return;
    if (state?.sessionId === paramId) {
      autoJoinTried.current = true;
      return;
    }
    if (autoJoinTried.current) return;
    if (roomPhase !== "exists") return;
    const stored = readStoredIdentity(paramId);
    if (stored) {
      autoJoinTried.current = true;
      joinSession(paramId, stored.name, { playerId: stored.playerId });
    }
  }, [paramId, joinSession, state?.sessionId, roomPhase]);

  useEffect(() => {
    if (lastError !== "Session not found") return;
    if (roomPhase !== "exists") return;
    setRoomPhase("missing");
    clearLastError();
  }, [lastError, roomPhase, clearLastError]);

  const joinWithRole = useCallback(
    (role: PlayerRole, name: string) => {
      if (!paramId) return;
      autoJoinTried.current = true;
      joinSession(paramId, name, { role });
    },
    [paramId, joinSession]
  );

  const startMissingAs = useCallback(
    (role: PlayerRole, name: string) => {
      if (!paramId) return;
      createSession(name, { sessionId: paramId, role });
    },
    [paramId, createSession]
  );

  const onLeave = () => {
    if (paramId) {
      sessionStorage.removeItem(`pointingBlackjack:${paramId}`);
    }
    leaveTable();
    navigate(lobbyPath());
  };

  const invalidRoomLinkSection = (
    <div className="pb-session">
      <section className="pb-panel">
        <h2>Invalid room link</h2>
        <p className="pb-muted">
          The room code in the URL must be 4–64 characters, only letters, numbers,
          dashes, and underscores (no spaces, dots, or slashes).
        </p>
        <button type="button" className="pb-button pb-button--primary" onClick={onLeave}>
          Back to lobby
        </button>
      </section>
    </div>
  );

  if (!paramId) {
    return <p className="pb-error">Missing session.</p>;
  }

  /** Bad code shape: never open a socket or show “can’t reach server”. */
  if (!isValidRoomCode(paramId)) {
    return invalidRoomLinkSection;
  }

  const showGameOver = state?.gameOver;

  if (!state && !showGameOver) {
    if (roomPhase === "loading") {
      return (
        <div className="pb-session pb-session--loading">
          <p>Checking room…</p>
        </div>
      );
    }

    if (roomPhase === "unreachable") {
      return (
        <div className="pb-session">
          <section className="pb-panel">
            <h2>Couldn&apos;t load this room</h2>
            <p className="pb-muted">
              First, check the address: the room code must be 4–64 characters (letters, numbers,
              dashes, underscores). Typos, extra segments, or pasted page URLs often look fine
              but aren&apos;t a real table link.
            </p>
            <p className="pb-muted">
              If the link is right, the app couldn&apos;t open the live WebSocket (
              <code className="pb-code">/pointy-ws</code>). For local dev, run{" "}
              <code className="pb-code">npm run dev</code>, or{" "}
              <code className="pb-code">npm start</code> and{" "}
              <code className="pb-code">npm run server</code> in two terminals.
            </p>
            <div className="pb-card-actions">
              <button
                type="button"
                className="pb-button pb-button--primary"
                onClick={retryRoomProbe}
              >
                Try again
              </button>
              <button type="button" className="pb-button pb-button--ghost" onClick={onLeave}>
                Back to lobby
              </button>
            </div>
          </section>
        </div>
      );
    }

    if (roomPhase === "invalid") {
      return invalidRoomLinkSection;
    }

    if (roomPhase === "missing") {
      return (
        <div className="pb-session">
          <section className="pb-panel">
            <h2>No table here yet</h2>
            <p className="pb-muted">
              Room <strong className="pb-code">{paramId}</strong> isn&apos;t active. You can
              start it and keep this exact link.
            </p>
            <SessionEntryForm
              busy={connectionStatus === "connecting"}
              buttons={MISSING_START_BUTTONS}
              onSubmit={({ role, name }) => {
                startMissingAs(role, name);
              }}
            />
            {lastError ? <p className="pb-error">{lastError}</p> : null}
          </section>
        </div>
      );
    }

    const stored = readStoredIdentity(paramId);
    if (stored && !lastError) {
      return (
        <div className="pb-session pb-session--loading">
          <p>Connecting…</p>
        </div>
      );
    }

    return (
      <div className="pb-session">
        <section className="pb-panel">
          <h2>Join this table</h2>
          <p className="pb-muted">
            Room <strong className="pb-code">{paramId}</strong>
          </p>
          <SessionEntryForm
            busy={connectionStatus === "connecting"}
            buttons={JOIN_BUTTONS}
            onSubmit={({ role, name }) => {
              joinWithRole(role, name);
            }}
          />
          {lastError ? <p className="pb-error">{lastError}</p> : null}
        </section>
      </div>
    );
  }

  if (!state) {
    return null;
  }

  if (state.gameOver) {
    return (
      <div className="pb-session">
        <div className="pb-game-over" role="status">
          <h2>Table closed</h2>
          <p className="pb-muted">
            This session ends 2 hours after it starts. Start a new one from the lobby anytime.
          </p>
          <div className="pb-card-actions">
            <button type="button" className="pb-button pb-button--primary" onClick={onLeave}>
              Back to lobby
            </button>
            <button
              type="button"
              className="pb-button pb-button--ghost"
              onClick={() => setFeedbackOpen(true)}
            >
              Feedback
            </button>
          </div>
        </div>
        <PointyFeedbackModal
          isOpen={feedbackOpen}
          sessionId={state.sessionId}
          playerName={state.players.find((p) => p.id === state.myPlayerId)?.name}
          onClose={() => setFeedbackOpen(false)}
        />
      </div>
    );
  }

  const myVote = state.voteByPlayer[state.myPlayerId];
  const myNumeric =
    typeof myVote === "number"
      ? myVote
      : undefined;

  const atTable = playersAtTable(state.players);
  const playerIds = atTable.map((p) => p.id);
  const counts = buildCounts(state.voteByPlayer, playerIds);
  const avg = averageVote(state.voteByPlayer, playerIds);
  const roleAverages = ROLE_AVG_ORDER.map((role) => ({
    role,
    label: ROLE_AVG_LABEL[role],
    avg: averageVoteForRole(atTable, state.voteByPlayer, role),
    hasPlayers: atTable.some((p) => p.role === role),
  })).filter((row) => row.hasPlayers);

  const myPlayer = state.players.find((p) => p.id === state.myPlayerId);
  const myBrb = myPlayer?.brb === true;
  const funNamesOn = state.funNames !== false;
  const showFunNamesOptions = canToggleFunNames(myPlayer);
  const productPlayers = atTable.filter((p) => p.role === "product");
  const teamPlayers = atTable.filter((p) => p.role !== "product");
  const teamParticipation = teamVoteParticipation(
    atTable,
    state.voteByPlayer
  );

  const renderVoteTableBody = (players: PlayerRow[]) =>
    players.map((p) => {
      const voted = hasSubmittedVote(state.voteByPlayer[p.id]);
      const brb = p.brb === true;
      const votedLabel = statusWithCountLabel("Voted", p.id, state.liveVoteCounts);
      const waitingLabel = statusWithCountLabel("Waiting", p.id, state.liveVoteCounts);
      return (
        <tr key={p.id}>
          <td>
            <PlayerNameCell player={p} brb={brb} funNames={funNamesOn} />
          </td>
          <td
            aria-label={
              voted
                ? state.liveVoteCounts
                  ? `${votedLabel}; value hidden until reveal`
                  : "Has voted; value hidden until reveal"
                : brb
                  ? "Be right back"
                  : state.liveVoteCounts
                    ? waitingLabel
                    : "Waiting to vote"
            }
          >
            {voted ? (
              <span className="pb-table__vote pb-table__vote--yes">
                {votedLabel}
              </span>
            ) : brb ? (
              <span className="pb-table__vote pb-table__vote--brb">BRB</span>
            ) : (
              <span className="pb-table__vote pb-table__vote--no">{waitingLabel}</span>
            )}
          </td>
        </tr>
      );
    });

  const renderVoteList = (players: PlayerRow[]) =>
    players.map((p) => {
      const v = state.voteByPlayer[p.id];
      const has = typeof v === "number";
      const brb = p.brb === true;
      return (
        <li key={p.id} className="pb-vote-row">
          <span className="pb-vote-row__name">
            <PlayerNameCell player={p} brb={brb} funNames={funNamesOn} />
          </span>
          <span className="pb-vote-row__val">
            {has ? (
              formatVoteDisplay(v, true)
            ) : brb ? (
              <span className="pb-vote-brb-label">BRB</span>
            ) : showNoVoteFrown(p.role) ? (
              <>
                <span className="pb-frown" aria-hidden>
                  ☹️
                </span>{" "}
                no vote
              </>
            ) : null}
          </span>
        </li>
      );
    });

  const renderProductTableBody = (players: PlayerRow[]) =>
    renderVoteTableBody(players);

  const renderProductList = (players: PlayerRow[]) =>
    renderVoteList(players);

  return (
    <div className="pb-session">
      <>
        <div className="pb-session__toolbar">
          <div className="pb-session__room">
            <span className="pb-muted">Room:</span>
            <code className="pb-code">{state.sessionId}</code>
            <button
              type="button"
              className={`pb-session__copy-link${copied ? " pb-session__copy-link--copied" : ""}`}
              onClick={copyLink}
              aria-label={copied ? "Invite link copied" : "Copy invite link"}
              title={copied ? "Copied!" : "Copy invite link"}
            >
              <FontAwesomeIcon icon={copied ? faCheck : faLink} />
            </button>
            {connectionStatus !== "open" ? (
              <span className="pb-session__connection" role="status">
                {connectionStatus === "connecting" ? "Reconnecting…" : "Disconnected"}
              </span>
            ) : null}
          </div>
          <div className="pb-session__actions">
            <button
              type="button"
              className={`pb-button pb-button--ghost ${myBrb ? "pb-button--brb-active" : ""}`}
              onClick={() => setBrb(!myBrb)}
            >
              {myBrb ? "I'm back" : "BRB"}
            </button>
            <button
              type="button"
              className="pb-button pb-button--ghost"
              onClick={() => setFeedbackOpen(true)}
            >
              Feedback
            </button>
            <button
              type="button"
              className="pb-button pb-button--ghost pb-button--leave"
              onClick={onLeave}
            >
              Leave
            </button>
          </div>
        </div>


        {!state.revealed ? (
          <div className="pb-voting-layout">
            <div className="pb-voting-layout__sidebar">
              <div className="pb-voting-layout__actions pb-card-actions">
                <button
                  type="button"
                  className="pb-button pb-button--accent"
                  onClick={reveal}
                >
                  Reveal cards
                </button>
              </div>

              {myPlayer ? (
                <YouCard
                  name={myPlayer.name}
                  funLabel={funDisplayName(myPlayer.name, myPlayer.funTitle, funNamesOn)}
                  role={myPlayer.role}
                  onSave={updateName}
                />
              ) : null}

              <section className="pb-panel pb-players">
                <h2>Players</h2>
                <div className="pb-table-wrap">
                  <table className="pb-table">
                    <thead>
                      <tr>
                        <th scope="col">Player</th>
                        <th scope="col">Vote</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamPlayers.length > 0 ? (
                        renderVoteTableBody(teamPlayers)
                      ) : (
                        <tr>
                          <td colSpan={2} className="pb-muted">
                            No Dev or QA players yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              {productPlayers.length > 0 ? (
                <section className="pb-panel pb-product">
                  <h2>Dealers</h2>
                  <div className="pb-table-wrap">
                    <table className="pb-table pb-table--product">
                      <thead>
                        <tr>
                          <th scope="col">Player</th>
                          <th scope="col">Vote</th>
                        </tr>
                      </thead>
                      <tbody>{renderProductTableBody(productPlayers)}</tbody>
                    </table>
                  </div>
                </section>
              ) : null}

              {showFunNamesOptions ? (
                <FunNamesOptions enabled={funNamesOn} onChange={setFunNames} />
              ) : null}
            </div>

            <section className="pb-panel pb-voting-layout__cards">
              <TeamVotePercent participation={teamParticipation} />
              <VoteCardGrid myNumeric={myNumeric} vote={vote} clearVote={clearVote} />
              {myPlayer?.role === "product" ? (
                <p className="pb-muted pb-product-vote-note">
                  Product is generally discouraged from voting.
                </p>
              ) : null}
            </section>
          </div>
        ) : (
          <section className="pb-panel pb-revealed">
            <div className="pb-revealed__actions">
              <button type="button" className="pb-button pb-button--primary" onClick={newRound}>
                Next round
              </button>
            </div>
            <div className="pb-revealed-layout">
              <div className="pb-revealed-layout__aside">
                <FrequencyBars counts={counts} />
                <div className="pb-avg-column">
                  <h3>Average</h3>
                  {avg !== null ? (
                    <p className="pb-avg-value" aria-label="Overall average">
                      {formatAverage(avg)}
                    </p>
                  ) : (
                    <p className="pb-muted">No votes yet.</p>
                  )}
                  {roleAverages.length > 0 ? (
                    <ul className="pb-avg-by-role" aria-label="Average by role">
                      {roleAverages.map(({ role, label, avg: roleAvg }) => (
                        <li key={role} className="pb-avg-by-role__row">
                          <span className={`pb-flair pb-flair--${role}`}>
                            {label}
                          </span>
                          <span
                            className={
                              roleAvg === null
                                ? "pb-avg-by-role__value pb-muted"
                                : "pb-avg-by-role__value"
                            }
                          >
                            {formatAverage(roleAvg)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <p className="pb-muted pb-avg-note">Non-voters excluded.</p>
                </div>
                <div className="pb-votes-column">
                  <h3>Players</h3>
                  <ul className="pb-vote-list">
                    {teamPlayers.length > 0 ? (
                      renderVoteList(teamPlayers)
                    ) : (
                      <li className="pb-muted">No QA or Dev players yet.</li>
                    )}
                  </ul>
                </div>
                {productPlayers.length > 0 ? (
                  <div className="pb-votes-column pb-product">
                    <h3>Dealers</h3>
                    <ul className="pb-vote-list">{renderProductList(productPlayers)}</ul>
                  </div>
                ) : null}
                {showFunNamesOptions ? (
                  <FunNamesOptions enabled={funNamesOn} onChange={setFunNames} />
                ) : null}
              </div>
              <section className="pb-panel pb-revealed-layout__cards">
                <TeamVotePercent participation={teamParticipation} />
                <VoteCardGrid myNumeric={myNumeric} vote={vote} clearVote={clearVote} />
                {myPlayer?.role === "product" ? (
                  <p className="pb-muted pb-product-vote-note">
                    Product is generally discouraged from voting.
                  </p>
                ) : null}
              </section>
            </div>
          </section>
        )}
      </>
      <PointyFeedbackModal
        isOpen={feedbackOpen}
        sessionId={state.sessionId}
        playerName={myPlayer?.name}
        onClose={() => setFeedbackOpen(false)}
      />
    </div>
  );
};
